import { groupBy, values } from 'underscore';
import type Backbone from 'backbone';
import { ProductType } from '@ve/types/ProductType';
import type { IBackboneProduct } from '@ve/types/backboneModels/Domain/ObjectValue';
import {
    buildRoomProviders,
    getSupplierType,
    type ProviderOffer,
    type ProviderOfferData,
} from '@features/roomProviders';
import groupProductsByCriterias from '../../groupProductsByCriterias';

export const SUPPLIER_BLOCKS_LIMIT = 20;

interface SupplierEntry {
    supplier_type: string;
    operator_id: string;
    min_price: string;
    position: string;
}

interface SupplierBlock {
    room_name: string;
    room_standart_name: string;
    room_category_id: string;
    meal_id: string;
    beds: string;
    nights: string;
    nightsInHotel: string;
    suppliers: SupplierEntry[];
}

interface SupplierBlocksPerDate {
    departure_date: string;
    blocks: SupplierBlock[];
}

const groupValues = <T, K extends string | number>(items: T[], keyOf: (item: T) => K): T[][] =>
    values(groupBy(items, keyOf));

const isHotelOfferProduct = (tour: IBackboneProduct): boolean => tour.get('type') === ProductType.HotelOfferProduct;

const getProductModel = (tour: IBackboneProduct) => tour.get('product');
const getOperatorId = (tour: IBackboneProduct): number => getProductModel(tour)?.get('operator')?.id ?? 0;
const getOperatorName = (tour: IBackboneProduct): string => getProductModel(tour)?.get('operator')?.name || '';
const getPrice = (tour: IBackboneProduct): number => getProductModel(tour)?.get('price') ?? 0;
const getBookingUrl = (tour: IBackboneProduct): string | undefined => getProductModel(tour)?.get('bookingUrl');
const getCheckInDate = (tour: IBackboneProduct): string => getProductModel(tour)?.get('checkInDate') || '';
const getRoomName = (tour: IBackboneProduct): string => getProductModel(tour)?.get('room')?.name || 'null';
const getRoomStandartName = (tour: IBackboneProduct): string => getProductModel(tour)?.get('room')?.standardName || 'null';
const getRoomCategoryId = (tour: IBackboneProduct): string => {
    const category = getProductModel(tour)?.get('room')?.category;
    return category != null ? `${category}` : '';
};
const getBeds = (tour: IBackboneProduct): string => getProductModel(tour)?.get('room')?.beds || '';
const getMealId = (tour: IBackboneProduct): string => {
    const meal = getProductModel(tour)?.get('meal');
    return meal ? `${meal.get('id') ?? ''}` : '';
};
const getNights = (tour: IBackboneProduct): string => `${getProductModel(tour)?.get('nights') || 0}`;
const getNightsInHotel = (tour: IBackboneProduct): string => {
    const product = getProductModel(tour);
    return `${product?.get('nightsInHotel') || product?.get('nights') || 0}`;
};

const buildSupplier = (offer: ProviderOfferData, position: number): SupplierEntry => ({
    supplier_type: getSupplierType(offer.operatorId),
    operator_id: `${offer.operatorId}`,
    min_price: `${offer.price}`,
    position: `${position}`,
});

const buildBlock = (toursOfSameBlock: IBackboneProduct[]): SupplierBlock => {
    const [tour] = toursOfSameBlock;
    const offers: ProviderOffer[] = groupValues(toursOfSameBlock, getOperatorId).map((group) => ({
        operatorId: getOperatorId(group[0]),
        operatorName: getOperatorName(group[0]),
        price: Math.min(...group.map(getPrice)),
        bookingUrl: getBookingUrl(group[0]),
    }));

    return {
        room_name: getRoomName(tour),
        room_standart_name: getRoomStandartName(tour),
        room_category_id: getRoomCategoryId(tour),
        meal_id: getMealId(tour),
        beds: getBeds(tour),
        nights: getNights(tour),
        nightsInHotel: getNightsInHotel(tour),
        suppliers: buildRoomProviders(offers).map((offer, index) => buildSupplier(offer, index + 1)),
    };
};

const buildEntryForDate = (toursOfSameDate: IBackboneProduct[]): SupplierBlocksPerDate => ({
    departure_date: getCheckInDate(toursOfSameDate[0]),
    blocks: groupProductsByCriterias(toursOfSameDate, ProductType.HotelOfferProduct)
        .slice(0, SUPPLIER_BLOCKS_LIMIT)
        .map((group) => buildBlock(group)),
});

const getSupplierBlocksPerDate = (
    foundTours?: Backbone.Collection<IBackboneProduct> | null,
): SupplierBlocksPerDate[] => {
    const products = (foundTours?.models ?? []).filter(isHotelOfferProduct);
    return groupValues(products, getCheckInDate).map((group) => buildEntryForDate(group));
};

export default getSupplierBlocksPerDate;
